// contractGrade — letter-grade computation for a managed contract's
// three-pillar performance model (deliverables / milestones / KPIs).
//
// Mirrors backend/src/utils/contractGrade.ts exactly so the Details-tab
// grade and the Contract Register grade column agree against the same
// data. If you change one, change the other in the same commit.
//
// Computation:
//   1. Build the REPORTING set: pillars whose sample is ≥ MIN_SAMPLE
//      and whose score is non-null. Pillars below the threshold are
//      excluded — a contract without milestones (or with too few)
//      still gets a meaningful grade from the pillars that DO have
//      data. Awaiting-data label only when zero pillars are reporting.
//   2. Equal-weighted average over the REPORTING pillars (1, 2, or 3).
//   3. Map average to a raw letter grade using bands shifted by the
//      org-level threshold offset (default 0). Positive offset =
//      stricter (each band requires a higher score); negative offset
//      = looser. Allowed range -30 to +10.
//        avg ≥ 90+offset → A
//        avg ≥ 80+offset → B
//        avg ≥ 70+offset → C
//        avg ≥ 60+offset → D
//        avg <  60+offset → E
//   4. Tiered floor cap based on critical-pillar count among
//      REPORTING pillars (a "critical" pillar is one whose score is
//      below the contract's watch threshold — same threshold that
//      paints the dial red). Awaiting-data pillars are unknown, not
//      critical, so they don't count toward the cap.
//        1 critical → cap at C
//        2 critical → cap at D
//        3 critical → cap at E
//   5. Final = worst of (raw, cap). Higher numeric severity wins —
//      raw can pull the grade DOWN below the cap, but cannot push it
//      ABOVE the cap.

export type Grade = "A" | "B" | "C" | "D" | "E";

export interface PillarData {
  score: number | null;
  sampleSize: number;
}

export interface GradeResult {
  /** null = awaiting data (zero pillars reporting). */
  grade: Grade | null;
  /** Equal-weighted average over reporting pillars, rounded to one
   *  decimal. Null when no pillar reports. */
  score: number | null;
  /** Human-readable label paired with the grade. */
  label: string;
  /** Populated only when the floor rule actually capped the raw grade. */
  capReason?: string;
  /** Number of pillars (0–3) included in the average. When < 3, the
   *  grade is partial and the surface should annotate accordingly. */
  reportingPillarCount: number;
}

/** Awaiting-data label is locked across all surfaces. */
export const AWAITING_DATA_LABEL = "Awaiting data";

// One-line definition of the supplier grade roll-up. Mirrors the backend
// SUPPLIER_GRADE_HELP (backend/src/utils/contractGrade.ts) so the FE help text
// describes the BE computation exactly. If you change one, change the other.
export const SUPPLIER_GRADE_HELP =
  "Value-weighted average of this supplier's active, graded contracts " +
  "(A best … E worst); suppliers with no graded active contracts show no grade.";

// Supplier-grade word vocabulary, distinct from the contract pillar labels
// (GRADE_LABELS) — a supplier's roll-up reads as a standing/relationship word
// rather than a per-contract performance verdict. Shown beside the compact
// grade badge on the supplier detail Performance stat.
export const SUPPLIER_GRADE_LABELS: Record<Grade, string> = {
  A: "Strong",
  B: "Good",
  C: "Fair",
  D: "Poor",
  E: "Critical",
};

/** The supplier grade word for a letter, or undefined when ungraded. Passed as
 *  GradeLetter's `label` override so the badge title/aria reads the supplier
 *  vocabulary (Strong/Fair/…), matching the visible word, instead of the
 *  contract pillar labels. */
export function supplierGradeWord(
  letter: Grade | null | undefined,
): string | undefined {
  return letter ? SUPPLIER_GRADE_LABELS[letter] : undefined;
}

/** Adapt a supplier's value-weighted grade roll-up into the shared GradeResult
 *  shape that GradeLetter consumes. A null letter (partial/unavailable rollup)
 *  produces the "—" awaiting-data tile. Used on both the SupplierRegister and
 *  the SupplierDetail so the two surfaces stay in structural lockstep. */
export function gradeResultForRollup(
  rollup: { letter: Grade | null } | null,
): GradeResult {
  const letter = rollup?.letter ?? null;
  return {
    grade: letter,
    score: null,
    label: letter ? GRADE_LABELS[letter] : AWAITING_DATA_LABEL,
    reportingPillarCount: 0,
  };
}

/** Locked label vocabulary. Don't deviate without updating SKILL.md §9.7. */
export const GRADE_LABELS: Record<Grade, string> = {
  A: "Excellent",
  B: "Good",
  C: "Monitor",
  D: "Needs improvement",
  E: "Critical",
};

// Numeric severity ramp: A best (1) … E worst (5). Exported so register
// surfaces (contract + supplier) sort grades by severity rather than by
// letter string, with a null/awaiting sentinel pinned last.
export const GRADE_SEVERITY: Record<Grade, number> = {
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  E: 5,
};
const SEVERITY_GRADE: Record<number, Grade> = {
  1: "A",
  2: "B",
  3: "C",
  4: "D",
  5: "E",
};

// Minimum sample size for a pillar to count as "reporting". Below this
// the pillar is excluded from the average. Set to 2 — a single data
// point is noisy, but two is enough signal to inform the grade.
const MIN_SAMPLE = 2;

function rawGrade(avg: number, offset: number): Grade {
  if (avg >= 90 + offset) return "A";
  if (avg >= 80 + offset) return "B";
  if (avg >= 70 + offset) return "C";
  if (avg >= 60 + offset) return "D";
  return "E";
}

function capGrade(criticalCount: number): Grade | null {
  if (criticalCount === 1) return "C";
  if (criticalCount === 2) return "D";
  if (criticalCount >= 3) return "E";
  return null;
}

/**
 * Compute the contract letter grade.
 *
 * @param deliverables    Deliverables pillar (score 0–100, sampleSize).
 * @param milestones      Milestones pillar (score 0–100, sampleSize).
 * @param kpis            KPIs pillar (score 0–100, sampleSize).
 * @param watchThreshold  Per-contract watch threshold; pillars below
 *                        this count as "critical" for the floor rule.
 *                        Defaults to 70 (matches the SKILL.md default).
 * @param offset          Org-level offset added to all five band
 *                        boundaries. Defaults to 0.
 */
export function computeContractGrade(
  deliverables: PillarData,
  milestones: PillarData,
  kpis: PillarData,
  watchThreshold = 70,
  offset = 0,
): GradeResult {
  // Build the reporting set — pillars with enough sample AND a non-null
  // score. Pillars without data are excluded from the average rather
  // than blocking the grade entirely.
  const reporting: number[] = [];
  for (const p of [deliverables, milestones, kpis]) {
    if (p.sampleSize >= MIN_SAMPLE && p.score !== null) {
      reporting.push(p.score);
    }
  }

  if (reporting.length === 0) {
    return {
      grade: null,
      score: null,
      label: AWAITING_DATA_LABEL,
      reportingPillarCount: 0,
    };
  }

  const avg = reporting.reduce((a, b) => a + b, 0) / reporting.length;
  const raw = rawGrade(avg, offset);

  // Critical count among reporting pillars only — a pillar awaiting
  // data is unknown, not critical.
  const criticalCount = reporting.filter((s) => s < watchThreshold).length;
  const cap = capGrade(criticalCount);

  let finalGrade = raw;
  let capReason: string | undefined;

  if (cap !== null) {
    const rawSeverity = GRADE_SEVERITY[raw];
    const capSeverity = GRADE_SEVERITY[cap];
    if (capSeverity > rawSeverity) {
      finalGrade = SEVERITY_GRADE[capSeverity];
      const pillarWord = criticalCount === 1 ? "pillar" : "pillars";
      capReason = `${criticalCount} critical ${pillarWord} below watch threshold`;
    }
  }

  return {
    grade: finalGrade,
    score: Math.round(avg * 10) / 10,
    label: GRADE_LABELS[finalGrade],
    reportingPillarCount: reporting.length,
    ...(capReason ? { capReason } : {}),
  };
}
